
import React, { useState } from 'react';
import { User, Lock, UserPlus, LogIn, GraduationCap, ChevronLeft, ArrowRight } from 'lucide-react';
import { User as UserType } from '../data/types';
import { AVATAR_OPTIONS, APP_VERSION } from './Common';

interface AuthViewProps {
    users: UserType[];
    onLogin: (user: UserType) => void;
    onRegister: (user: UserType) => void;
}

export const AuthView = ({ users, onLogin, onRegister }: AuthViewProps) => {
    const [view, setView] = useState<'grid' | 'password' | 'register'>('grid');
    const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
    const [passwordInput, setPasswordInput] = useState('');
    const [error, setError] = useState('');

    // Register State
    const [regUsername, setRegUsername] = useState('');
    const [regPassword, setRegPassword] = useState('');
    const [regName, setRegName] = useState('');
    const [regAvatar, setRegAvatar] = useState('🐶');

    const handleUserClick = (user: UserType) => {
        // Quick login if password is '0000'
        if (user.password === btoa('0000')) {
            onLogin(user);
        } else {
            setSelectedUser(user);
            setView('password');
            setError('');
            setPasswordInput('');
        }
    };

    const handlePasswordSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedUser) return;
        
        if (selectedUser.password === btoa(passwordInput)) {
            onLogin(selectedUser);
        } else {
            setError('密碼錯誤，請重試');
        }
    };

    const handleRegister = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!regUsername || !regPassword || !regName) {
            setError('請填寫所有欄位');
            return;
        }

        if (users.some(u => u.username === regUsername)) {
            setError('此帳號已被使用');
            return;
        }

        const newUser: UserType = {
            id: Date.now().toString(),
            username: regUsername,
            name: regName,
            password: btoa(regPassword),
            avatar: regAvatar
        };

        onRegister(newUser);
    };

    return (
        <div className="min-h-screen bg-fudan-light flex flex-col items-center justify-center p-4">
            <div className="mb-8 text-center animate-fade-in-up">
                <div className="w-20 h-20 bg-fudan-blue rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <GraduationCap className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-3xl font-serif font-bold text-fudan-blue mb-2">全方位複習系統</h1>
                <p className="text-gray-600">
                    {view === 'grid' && "請選擇使用者以登入"}
                    {view === 'password' && `歡迎回來，${selectedUser?.name}`}
                    {view === 'register' && "建立新學習檔案"}
                </p>
            </div>

            <div className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-fade-in-up delay-100 min-h-[400px] flex flex-col">
                
                {/* View 1: User Grid */}
                {view === 'grid' && (
                    <div className="p-8 flex flex-col h-full">
                        {users.length > 0 ? (
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                {users.map(u => (
                                    <button 
                                        key={u.id}
                                        onClick={() => handleUserClick(u)}
                                        className="flex flex-col items-center justify-center p-6 bg-gray-50 border-2 border-transparent hover:border-fudan-blue hover:bg-blue-50 rounded-xl transition-all group"
                                    >
                                        <div className="text-5xl mb-3 transform group-hover:scale-110 transition-transform">
                                            {u.avatar || '🎓'}
                                        </div>
                                        <span className="font-bold text-gray-700 group-hover:text-fudan-blue">{u.name}</span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center text-gray-400 py-10">
                                尚無使用者，請先建立帳號
                            </div>
                        )}
                        
                        <div className="mt-auto border-t border-gray-100 pt-6">
                            <button 
                                onClick={() => setView('register')}
                                className="w-full py-3 bg-white border-2 border-dashed border-gray-300 text-gray-500 font-bold rounded-xl hover:border-fudan-blue hover:text-fudan-blue transition-colors flex items-center justify-center"
                            >
                                <UserPlus className="w-5 h-5 mr-2" />
                                建立新使用者
                            </button>
                        </div>
                    </div>
                )}

                {/* View 2: Password Input */}
                {view === 'password' && (
                    <div className="p-8 flex flex-col h-full relative">
                        <button 
                            onClick={() => setView('grid')}
                            className="absolute top-4 left-4 text-gray-400 hover:text-gray-600"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>

                        <div className="flex flex-col items-center justify-center flex-1">
                            <div className="text-6xl mb-4 animate-bounce-in">
                                {selectedUser?.avatar}
                            </div>
                            <h2 className="text-xl font-bold text-gray-800 mb-6">{selectedUser?.name}</h2>

                            <form onSubmit={handlePasswordSubmit} className="w-full">
                                {error && (
                                    <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg text-center font-bold">
                                        {error}
                                    </div>
                                )}
                                <div className="relative mb-6">
                                    <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                    <input 
                                        type="password" 
                                        value={passwordInput}
                                        onChange={(e) => setPasswordInput(e.target.value)}
                                        className="w-full pl-10 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-fudan-blue outline-none text-lg"
                                        placeholder="請輸入密碼"
                                        autoFocus
                                    />
                                </div>
                                <button 
                                    type="submit"
                                    className="w-full py-4 bg-fudan-blue hover:bg-blue-800 text-white font-bold rounded-xl shadow-lg transition-transform transform active:scale-95 flex items-center justify-center"
                                >
                                    登入
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                {/* View 3: Register */}
                {view === 'register' && (
                    <div className="p-8 flex flex-col h-full relative">
                         <button 
                            onClick={() => setView('grid')}
                            className="absolute top-4 left-4 text-gray-400 hover:text-gray-600"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                        <h2 className="text-xl font-bold text-center mb-6">建立新帳號</h2>

                        <form onSubmit={handleRegister} className="space-y-4">
                            {error && (
                                <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg text-center font-bold">
                                    {error}
                                </div>
                            )}

                            <div>
                                <label className="block text-gray-700 text-xs font-bold mb-1">選擇頭像</label>
                                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                                    {AVATAR_OPTIONS.map(avi => (
                                        <button
                                            key={avi}
                                            type="button"
                                            onClick={() => setRegAvatar(avi)}
                                            className={`w-10 h-10 flex items-center justify-center text-2xl rounded-lg flex-shrink-0 transition-all ${regAvatar === avi ? 'bg-blue-100 ring-2 ring-fudan-blue' : 'bg-gray-50 hover:bg-gray-100'}`}
                                        >
                                            {avi}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-gray-700 text-xs font-bold mb-1">顯示名稱 (例如: 王小明)</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                                    <input 
                                        type="text" 
                                        value={regName}
                                        onChange={(e) => setRegName(e.target.value)}
                                        className="w-full pl-9 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fudan-blue outline-none"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-gray-700 text-xs font-bold mb-1">登入帳號</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                                    <input 
                                        type="text" 
                                        value={regUsername}
                                        onChange={(e) => setRegUsername(e.target.value)}
                                        className="w-full pl-9 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fudan-blue outline-none"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-gray-700 text-xs font-bold mb-1">密碼</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                                    <input 
                                        type="password" 
                                        value={regPassword}
                                        onChange={(e) => setRegPassword(e.target.value)}
                                        className="w-full pl-9 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fudan-blue outline-none"
                                    />
                                </div>
                            </div>

                            <button 
                                type="submit"
                                className="w-full py-3 bg-fudan-blue hover:bg-blue-800 text-white font-bold rounded-lg shadow-md mt-4"
                            >
                                完成註冊
                            </button>
                        </form>
                    </div>
                )}
            </div>
            
            <div className="mt-8 text-gray-400 text-xs">
                © 2026 全方位複習系統 v{APP_VERSION}
            </div>
        </div>
    );
};
